import { createAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment/moment';
import API from '../../app/api';
import APIForm from '../../app/apiForm';
import { getECS, getECSToEdeu, prepareData } from '../../pages/ward/forms/ecs/ecs';
import Notes from '../../util/notes';
import { PATIENT_CONTEXT_TYPE } from '../../util/patientContextUtil';
import { setSnackbar } from './globals';
import { addNotesForPatient } from './notes';
import { cleaningRequestPath, isInCurrentWard, updatePatientData } from './ward';
import { getPatientFlag } from '../../pages/ward/mainScreen/util/patientFlagsUtil';
import { getConfig } from '../../util/configUtil';
import { modifyValuesInPatientInWard } from './patientContext';
import { getWardsIdForPatientsSearch } from './wardMap';
import { sendUserAudit } from './user';
import { AUDIT_TYPES } from '../../util/auditUtil';
import { DISCHARGE_STRATEGY, DISCHARGE_TYPE, PATIENT_TAGS } from '../../util/patientUtil';
import researchPatientData from '../researchPatient';

export const patientSlice = createSlice({
    name: 'patient',
    initialState: {
        value: undefined,
        loading: undefined,
        loadingAdmission: undefined,
        transfering: undefined,
        demographicPatient: undefined,
        editPatientActivity: undefined,
        editFullPatientData: undefined,
        mergeQueuePatient: undefined,
        ptContextBorderEnable: false,
        forceClosePatientContext: false,
        returnToPatientAfterRegistry: null,
    },
    reducers: {
        setPatient: (state, patient) => {
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the Immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            state.value = patient.payload;
        },
        reloadFromServer: (state) => {},
        fetchPatientById: (state, patientId) => {},
        setLoading: (state, patientId) => {
            state.loading = patientId.payload;
        },
        setProcessing: (state, patientId) => {
            state.processing = patientId.payload;
        },
        setTransfering: (state, transferInfo) => {
            state.transfering = transferInfo.payload;
        },
        setPatientBed: (state, patient) => {
            state.patientBed = patient.payload;
        },
        setDemographicPatient: (state, patient) => {
            state.demographicPatient = patient.payload;
        },
        setEditPatientActivity: (state, patient) => {
            state.editPatientActivity = patient.payload;
        },
        setEditFullPatientData: (state, patient) => {
            state.editFullPatientData = patient.payload;
        },
        setMergeQueuePatient: (state, patient) => {
            state.mergeQueuePatient = patient.payload;
        },
        setLoadingAdmission: (state, patient) => {
            state.loadingAdmission = patient.payload;
        },
        setPtContextBorderEnable: (state, patient) => {
            state.ptContextBorderEnable = patient.payload;
        },
        setForceClosePatientContext: (state, patient) => {
            state.forceClosePatientContext = patient.payload;
        },
        setReturnToPatientAfterRegistry: (state, patient) => {
            state.returnToPatientAfterRegistry = patient.payload;
        },
    },
});

export const {
    setPatient,
    setLoading,
    setProcessing,
    fetchPatientById,
    setTransfering,
    setPatientBed,
    setDemographicPatient,
    setMergeQueuePatient,
    setLoadingAdmission,
    setPtContextBorderEnable,
    setForceClosePatientContext,
    setEditPatientActivity,
    setReturnToPatientAfterRegistry,
    setEditFullPatientData,
} = patientSlice.actions;

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched
// export const incrementAsync = amount => dispatch => {
//     setTimeout(() => {
//         dispatch(incrementByAmount(amount));
//     }, 1000);
// };

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const patient = (state) => state.patient.value;
export const loading = (state) => state.patient.loading;
export const processing = (state) => state.patient.processing;
export const transfering = (state) => state.patient.transfering;
export const patientBed = (state) => state.patient.patientBed;
export const demographicPatient = (state) => state.patient.demographicPatient;
export const editFullPatientData = (state) => state.patient.editFullPatientData;
export const mergeQueuePatient = (state) => state.patient.mergeQueuePatient;
export const loadingAdmission = (state) => state.patient.loadingAdmission;
export const ptContextBorderEnable = (state) => state.patient.ptContextBorderEnable;
export const forceClosePatientContext = (state) => state.patient.forceClosePatientContext;
export const editPatientActivity = (state) => state.patient.editPatientActivity;
export const returnToPatientAfterRegistry = (state) => state.patient.returnToPatientAfterRegistry;

export const dischargePatientCompleted = createAction('patient/dischargeCompleted');

export const loadPatientById = (patientId) => (dispatch, getState) => {
    if (getState().patient.loading !== patientId.toUpperCase()) {
        dispatch(setLoading(patientId.toUpperCase()));
        if (patientId === getConfig('modules.research.name', 'RESEARCH')) {
            console.log('research definition', researchPatientData);
            dispatch(setPatient(researchPatientData));
            dispatch(setLoading(undefined));
        } else {
            API.getPatientByID(patientId)
                .then((patient) => {
                    //if (getState().patient.loading === patientId) {
                    // may be that some other request will hang and we do not want this data !
                    dispatch(setPatient(patient));
                    dispatch(setLoading(undefined));
                    // }
                })
                .catch(function (error) {
                    // TODO: error handler !!!
                    console.error(error);
                });
        }
    }
};

export const removePatient = () => (dispatch) => {
    dispatch(setPatient(undefined));
    dispatch(setLoading(undefined));
};

export const transferPatient = (patientId, bedId, groupId) => async (dispatch) => {
    dispatch(setTransfering({ patientId: patientId, bedId: bedId, wardId: groupId }));
};

export const getPatientByPiwId = (piwId) => (dispatch, getState) => {
    if (!_.isEmpty(getState()?.ward?.patients)) {
        return _.find(getState().ward.patients, (pt) => pt?.__id === piwId);
    }
};

export const dischargePatient = (piw, dischargeObject, hospital, dischargeType, callbackFun) => async (dispatch) => {
    const hospitalId = hospital;
    const odsCode = hospitalId;
    const isUHDClient = getConfig('client', null) === 'UHD';
    const dodDateUpdateEnabled = getConfig('modules.patient.dodDateUpdateEnabled', false);
    const destination = dischargeObject?.destination?.destination || null;

    let dischargeStrategy = DISCHARGE_STRATEGY.DCH;
    let dischargeDate = `${dischargeObject?.value?.departureDate}T${dischargeObject?.value?.departureTime}`;
    let dischargeDisposition = dischargeObject?.value?.dischargeStatus?.snomedCode;
    let dischargeLocation = dischargeObject?.value?.dischargeDestination?.snomedCode;

    if (isUHDClient) {
        dischargeStrategy = DISCHARGE_STRATEGY.UHD;
        // if (dischargeObject?.isReadyToAdmit) dischargeStrategy = DISCHARGE_STRATEGY.UHD_PENDING;
        if (dischargeObject?.strategy) dischargeStrategy = dischargeObject?.strategy;

        dischargeDisposition = dischargeObject?.value?.dischargeStatus?.__description;
        dischargeLocation = dischargeObject?.value?.dischargeDestination?.__description;
    }

    if (dischargeStrategy === DISCHARGE_STRATEGY.UHD_MOVE) {
        if (_.isNil(dischargeObject?.value?.departureDate)) dischargeDate = moment().toISOString();
    }

    let clerkingClinician = null;
    if (piw?.firstClinician?.who?.__description) {
        clerkingClinician = piw?.firstClinician?.who?.__description;
    }

    let dischargePayload = {};

    // Send Discharge
    switch (dischargeType) {
        case DISCHARGE_TYPE.IP_DISCHARGE:
            dischargePayload = {
                dischargeStrategy,
                patientInWardId: piw?.__id,
                dischargeDisposition: dischargeObject?.dischargeMethod?.__code,
                dischargeLocation: dischargeObject?.dischargeDestination?.__code,
                dischargeDate: moment(dischargeObject?.dischargeDateTime).toISOString(),
                hospitalId,
                odsCode,
                dischargeSite: null,
                dischargingClinician: null,
                dischargeLetter: null,
                clerkingClinician: null,
            };
            break;
        case DISCHARGE_TYPE.ED_DISCHARGE:
            dischargePayload = {
                dischargeStrategy,
                patientInWardId: piw?.__id,
                dischargeDisposition,
                dischargeLocation,
                dischargeDate,
                hospitalId,
                odsCode,
                dischargeSite: dischargeObject?.value?.hospital ? dischargeObject?.value?.hospital?.__id : '',
                dischargingClinician: dischargeObject?.value?.who?.__id,
                dischargeLetter: dischargeObject?.ecs || '',
                clerkingClinician,
            };
            break;
        case DISCHARGE_TYPE.ED_TRANSFER_DISCHARGE:
            dischargePayload = {
                dischargeStrategy,
                patientInWardId: piw?.__id,
                dischargeDisposition,
                dischargeLocation,
                dischargeDate,
                hospitalId,
                odsCode,
                dischargeSite: dischargeObject?.value?.hospital ? dischargeObject?.value?.hospital?.__id : '',
                dischargingClinician: dischargeObject?.value?.who?.__id,
                dischargeLetter: dischargeObject?.ecs || '',
                clerkingClinician,
                autoEDAdmit: true,
                admissionWard: destination?.wardId ?? null,
            };
            break;
        default:
            console.error('UNKNOWN DISCHARGE TYPE', dischargeType);
    }

    API.discharge(dischargePayload)
        .then((response) => {
            if (response?.status === 200 && response?.data === true) {
                dispatch(sendUserAudit(piw, AUDIT_TYPES.DISCHARGE_PATIENT));

                if (piw?.patient?.tags?.includes(PATIENT_TAGS.RE_ATTENDANCE)) {
                    const tags = _.filter(piw?.patient?.tags, (t) => t !== PATIENT_TAGS.RE_ATTENDANCE);
                    API.updatePatientTags(piw?.patient?.__id, tags).catch((e) => {
                        console.error(e);
                        dispatch(setSnackbar({ type: 'error', message: 'Failed to update patient tags.' }));
                    });
                }

                if (dischargeObject.deceased && dodDateUpdateEnabled) {
                    const flag = getPatientFlag(piw, 'deceased');
                    if (flag?.deceasedDate) {
                        API.setDeceased(piw?.patient?.__id, flag.deceasedDate).catch((e) => {
                            console.error(e);
                            dispatch(
                                setSnackbar({
                                    type: 'error',
                                    message: 'Failed to update patient deceased status and date of death',
                                }),
                            );
                        });
                    }
                }

                dispatch(cleaningRequestPath(piw?.bed, 'discharge'));
                dispatch(generateVoD(piw));
                // dispatch(dischargePatientCompleted({piw}));

                if (callbackFun) callbackFun();
            }
        })
        .catch(function (error) {
            console.error('Error on discharge', error);
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'Failed to discharge patient. Please try again or contact support if the issue persists.',
                }),
            );
        });
};

const generateVoD = (currentPatient) => (dispatch, getState) => {
    const enabled = getConfig('verificationOfDeathAgyleMiniForm', false);
    if (enabled) {
        const objectToSave = _.get(currentPatient, 'studies.verificationOfDeath');
        if (objectToSave && currentPatient?.patient) {
            APIForm.saveDor(
                {
                    data: {
                        verificationOfDeath: objectToSave,
                        patient: currentPatient?.patient,
                        name: `${currentPatient?.patient?.familyName} ${currentPatient?.patient?.firstName}`,
                        nhs: currentPatient?.patient?.nhsNumber,
                    },
                },
                currentPatient?.patient?.hospitalNumber,
                'verificationOfDeath',
            ).catch((err) => {
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Failed to generate Verification of Death DOR.',
                    }),
                );
            });
        }
    }
};

// Old admission method
// TODO @Mateusz add audit
export const admitPatient = (currentPatient, dischargeObject, admissionWard, callbackFun) => (dispatch, getState) => {
    try {
        const dischargeForm = _.find(dischargeObject, { name: 'studies.discharge' })?.value;
        const transferForm = _.find(dischargeObject, { name: 'studies.transfer' })?.value;
        const transferInfo = _.cloneDeep(getState().patient.transfering);
        let modifications = [];
        let notes = [];

        let destination = {
            bedId: transferInfo?.bedId || '',
            bayId: transferInfo?.bayId || '',
            wardId: admissionWard || '0EDAU',
        };

        if (transferForm?.destination) {
            destination = {
                ...destination,
                bedId: transferForm?.destination?.bedId || destination.bedId,
                bayId: transferForm?.destination?.bayId || destination.bayId,
            };
        }

        dispatch(setLoadingAdmission(transferInfo?.patientId || currentPatient?.patient?.__id));

        _.forEach(dischargeObject, (set) => {
            modifications.push({ [set.name]: set.value });
            if (set.note) notes.push({ text: set.note, type: set.name });
        });

        // Save All Discharge forms
        dispatch(
            updatePatientData(currentPatient, modifications, (status) => {
                if (status) {
                    // Save Note
                    dispatch(
                        addNotesForPatient(
                            currentPatient?.__id,
                            currentPatient.patient.__id,
                            currentPatient?.admission?.consultantEpisodeId,
                            notes,
                            () => {
                                API.transfer({
                                    patientInWardId: currentPatient.__id,
                                    dischargeDisposition: dischargeForm.dischargeStatus?.snomedCode,
                                    dischargeLocation: dischargeForm.dischargeDestination?.snomedCode,
                                    dischargingClinician: dischargeForm.who.__id,
                                    admissionType: '28', //other
                                    consultingDoctor: transferForm?.consultant?.__id,
                                    consultingDoctorLocalCode: transferForm?.consultant?.pasCode,
                                    specialty: transferForm?.specialty?.__code,
                                    admissionSource: transferForm?.admissionSource?.__code,
                                    patientType: transferForm?.patientType?.__code,
                                    admissionWard: destination.wardId,
                                    admissionRoom: destination.bayId,
                                    admissionBed: destination.bedId,
                                    dischargeDate: `${dischargeForm.departureDate}T${dischargeForm.departureTime}`,
                                })
                                    .then((response) => {
                                        if (response?.status === 200 && response?.data === true) {
                                            dispatch(
                                                setSnackbar({
                                                    type: 'success',
                                                    message: 'Patient successfully admitted.',
                                                }),
                                            );
                                        } else {
                                            dispatch(
                                                setSnackbar({
                                                    type: 'error',
                                                    message: 'Failed to admit patient.',
                                                }),
                                            );
                                        }
                                    })
                                    .catch((error) => {
                                        dispatch(setSnackbar({ type: 'error', message: 'Failed to admit patient.' }));
                                        console.error('Error on transfer', error);
                                    })
                                    .finally(() => {
                                        if (callbackFun) callbackFun();
                                        dispatch(setLoadingAdmission(undefined));
                                    });

                                const clone = _.cloneDeep(currentPatient);
                                _.forEach(dischargeObject, (set) => {
                                    _.set(clone, set.name, set.value);
                                });
                                getECSToEdeu(clone);
                            },
                        ),
                    );
                } else {
                    dispatch(setSnackbar({ type: 'error', message: 'Failed to admit patient.' }));
                    dispatch(setLoadingAdmission(undefined));
                }
            }),
        );
        dispatch(cleaningRequestPath(currentPatient?.bed, 'transfer'));
    } catch (e) {
        console.error(e);
        dispatch(setLoadingAdmission(undefined));
        dispatch(setSnackbar({ type: 'error', message: 'Failed to admit patient.' }));
    }
};

// TODO @Mateusz add audit and replace admitPatient?
export const patientAdmission = (patient, admissionObject, callbackFun) => (dispatch) => {
    try {
        const admission = _.find(admissionObject, { name: 'studies.admissionForm' })?.value;
        const { destination, admissionDateTime, specialty, consultant, admissionSource, category, admissionMethod } = admission;
        let modifications = [];
        let notes = [];

        dispatch(setLoadingAdmission(patient?.patient?.__id));

        _.forEach(admissionObject, (set) => {
            modifications.push({ [set.name]: set.value });
            if (set.note) notes.push({ text: set.note, type: set.name });
        });

        // Save All Discharge forms
        dispatch(
            updatePatientData(patient, modifications, (status) => {
                if (status) {
                    // Save Note
                    dispatch(
                        addNotesForPatient(patient?.__id, patient.patient.__id, patient?.admission?.consultantEpisodeId, notes, () => {
                            API.transfer({
                                patientInWardId: patient.__id,
                                dischargeDate: moment(admissionDateTime).toISOString(),

                                specialty: specialty?.__code,
                                consultingDoctor: consultant?.__id,
                                consultingDoctorLocalCode: consultant?.pasCode || null,

                                admissionWard: destination.wardId || null,
                                admissionRoom: destination.bayId || null,
                                admissionBed: destination.bedId || null,

                                admissionType: admissionMethod?.__code,
                                admissionSource: admissionSource?.__code || null,
                                patientType: category?.__code,

                                dischargeDisposition: null, // TODO check this
                                dischargeLocation: null, // TODO check this
                                dischargingClinician: consultant?.__id, // TODO check this
                            })
                                .then((response) => {
                                    if (response?.status === 200 && response?.data === true) {
                                        dispatch(
                                            setSnackbar({
                                                type: 'success',
                                                message: 'Patient successfully admitted.',
                                            }),
                                        );
                                    } else {
                                        dispatch(
                                            setSnackbar({
                                                type: 'error',
                                                message: 'Failed to admit patient.',
                                            }),
                                        );
                                    }
                                })
                                .catch((error) => {
                                    dispatch(setSnackbar({ type: 'error', message: 'Failed to admit patient.' }));
                                    console.error('Error on transfer', error);
                                })
                                .finally(() => {
                                    if (callbackFun) callbackFun();
                                    dispatch(setLoadingAdmission(undefined));
                                });

                            const clone = _.cloneDeep(patient);
                            _.forEach(admissionObject, (set) => {
                                _.set(clone, set.name, set.value);
                            });
                            getECSToEdeu(clone);
                        }),
                    );
                } else {
                    dispatch(setSnackbar({ type: 'error', message: 'Failed to admit patient.' }));
                    dispatch(setLoadingAdmission(undefined));
                }
            }),
        );
        dispatch(cleaningRequestPath(patient?.bed, 'transfer'));
    } catch (e) {
        console.error(e);
        dispatch(setLoadingAdmission(undefined));
        dispatch(setSnackbar({ type: 'error', message: 'Failed to admit patient.' }));
    }
};

// Save patient in ward data mechanism
export const savePatientInWardData =
    (
        dataSet,
        patient,
        patientAddData,
        patientType,
        dischargeType = DISCHARGE_TYPE.NONE,
        hospitalId,
        pastAttendances,
        forcePastSaveApi,
        callbackFun,
    ) =>
    async (dispatch, getState) => {
        // Configs
        const dischargeEmailSendAddress = getConfig('dischargeEmailSendAddress', {});
        const client = getConfig('client');
        const casCardOnHL7 = getConfig('casCardOnHL7', false);
        const nurseLetterGenerateAfterDischarge = getConfig('nurseLetterGenerateAfterDischarge', false);

        const showNotification = (dataStatus, noteStatus) => {
            let message;
            let type = 'error';
            if (dataStatus) {
                if (noteStatus) {
                    message = 'Patient data stored successfully. Notes have been updated.';
                    type = 'success';
                } else {
                    message = 'The patient data has been saved correctly, but notes could not be saved.';
                }
            } else {
                message = 'Failed to update patient data and save notes. Please try again.';
            }
            dispatch(setSnackbar({ type: type, message: message }));
        };

        const createNotesForDiagnosisAndClinicians = (modifications, array) => {
            let patientWithoutModify = _.cloneDeep(patient);

            const seeAndTreat = _.find(modifications, { name: 'studies.seeAndTreat' });
            const rapidAssessment = _.find(modifications, { name: 'seen' });

            // Diagnosis Treatment and Investigations search
            if (
                _.some(modifications, (element) => {
                    return element.name === 'diagnosis' || element.name === 'triage';
                }) &&
                _.isNil(seeAndTreat) &&
                _.isNil(rapidAssessment)
            ) {
                const note = Notes.getDiagTreatInvestDependencyNote(modifications, patientWithoutModify);
                if (note !== '') array.unshift({ text: note, type: 'diagTreatInvest' });
            }

            // Clinician search
            if (
                _.some(modifications, (element) => {
                    return (
                        element.name === 'firstClinician' ||
                        element.name === 'seniorReview' ||
                        element.name === 'handover' ||
                        element.name === 'currentClinician' ||
                        element.name === 'ambulanceHandover' ||
                        element.name === 'studies.edau.nurseHandover' ||
                        element.name === 'studies.edau.currentNurse' ||
                        element.name === 'currentClinicianIP' ||
                        element.name === 'currentNurse'
                    );
                })
            ) {
                const note = Notes.getCliniciansNote(modifications, patientWithoutModify);
                array.unshift({ text: note, type: 'clinicians' });
            }
        };

        const createECS = (values, dischargeCallback, customTemplate) => {
            const clone = _.cloneDeep(patient);
            values.forEach((value) => {
                _.set(clone, value.name, value.value);
            });
            dispatch(sendUserAudit(clone, AUDIT_TYPES.OPEN_DOCUMENT, { pdfTemplate: customTemplate || 'ecs' }));
            getECS(clone, patientAddData, patientType, false, customTemplate || 'ecs', dischargeCallback).then((res) => {
                if (res) {
                    dispatch(
                        setSnackbar({
                            type: 'success',
                            message:
                                patientType !== PATIENT_CONTEXT_TYPE.EDAU
                                    ? 'Patient information and ECS saved successfully!'
                                    : 'EDAU patient documentation and record saved successfully!',
                        }),
                    );
                } else {
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message:
                                patientType !== PATIENT_CONTEXT_TYPE.EDAU
                                    ? 'Failed to save Emergency Care Summary!'
                                    : 'Failed to save EDAU Documentation DoR!',
                        }),
                    );
                }
            });
        };

        const dischargeEmailSend = async (values) => {
            const clone = _.cloneDeep(patient);
            values.forEach((value) => {
                _.set(clone, value.name, value.value);
            });

            const age = moment().diff(clone.patient?.dob, 'years');
            let emails = [];
            if (age < 5) {
                // Old Logic, which checking address lines for Poole

                //const address = _.find(patient?.patient?.addresses, (adr) => adr?.typeName !== 'Temporary');
                //const isPoole = _.some(address?.addressLines, (line) => _.toLower(line) === 'poole');
                //if (isPoole) {
                //    emails.push(dischargeEmailSendAddress.poole);
                //} else {
                //    emails.push(dischargeEmailSendAddress.other);
                //}

                emails.push(dischargeEmailSendAddress.emails);
            }

            const data = await prepareData(clone, null, 'healthVisitorReports');
            const dataToPass = {
                data: {
                    ...data,
                    emailToSend: _.isEmpty(emails)
                        ? []
                        : [
                              {
                                  mail: {
                                      name: 'email',
                                      emails: emails,
                                      external: true,
                                  },
                                  text: '',
                              },
                          ],
                },
            };
            APIForm.getSaveForm(dataToPass, clone.patient.hospitalNumber, 'healthVisitorReports');
        };

        const afterSave = (values) => {
            dispatch(modifyValuesInPatientInWard(values, patient));
            if (dischargeType !== DISCHARGE_TYPE.NONE) {
                let discharge = _.find(values, { name: 'studies.discharge' });
                const hospital = hospitalId || null;

                if (casCardOnHL7 && client === 'UHD') {
                    createECS(
                        values,
                        (pdf) => {
                            discharge = {
                                ...discharge,
                                ecs: pdf?.target?.result?.split(',')?.[1],
                            };

                            dispatch(dischargePatient(patient, discharge, hospital, dischargeType, callbackFun));
                        },
                        `nursing.notes${hospitalId === 'R0D01' ? 'PH' : 'RBH'}`,
                    );
                } else {
                    createECS(values, false);
                    dispatch(dischargePatient(patient, discharge, hospital, dischargeType, callbackFun));
                }
                if (nurseLetterGenerateAfterDischarge) {
                    dischargeEmailSend(values);
                }
            }

            const isEDAUDischarged = _.find(values, { name: 'metadata.EDAUDischarged' });
            const patientIsDischarged =
                patient?.admission?.status === 'DISCHARGE_REQUESTED' || patient?.admission?.status === 'TRANSFER_REQUESTED';
            const patientIsDischargedEdeu = patient?.metadata?.EDAUDischarged ? patient?.metadata?.EDAUDischarged : false;

            if (isEDAUDischarged || patient.metadata?.EDAUDischarged || patientIsDischarged || patientIsDischargedEdeu)
                createECS(values, false);
            if (callbackFun && dischargeType === DISCHARGE_TYPE.NONE) callbackFun(values);
        };

        const isLocked = getState().patientContext.isPatientLocked;
        let mod = [];
        let modDate = [];
        let notes = [];

        dataSet.forEach((val) => {
            if (!val?.onlyModify) {
                mod.push({ [val.name]: val.value });
                if (!!val.valueDate) modDate.push({ [val.name]: val.valueDate });

                if (val.noteToAdd) notes.push({ text: val.noteToAdd, type: val.noteType || val.name });
                if (val.note) notes.push({ text: val.note, type: val.noteType });
                if (val.noteToAddWithRefId) {
                    _.forEach(val.noteToAddWithRefId, (singleNote) => {
                        notes.push({
                            text: singleNote.noteToAddWithRefId,
                            type: val.name,
                            referenceId: singleNote.referenceId,
                        });
                    });
                }
            }
        });

        if (pastAttendances && !isLocked && patient?.studies?.dischargeLetter) {
            mod.push({ 'studies.dischargeLetter': null });
        }

        createNotesForDiagnosisAndClinicians(dataSet, notes);

        try {
            mod = _.filter(mod, (val) => !_.isUndefined(val[Object.keys(val)[0]]));
        } catch (er) {
            console.error(er);
        }
        const _isInCurrentWard = await dispatch(isInCurrentWard(patient?.__id));
        if (pastAttendances || forcePastSaveApi || !_isInCurrentWard) {
            if (!isLocked) {
                API.updatePatientInWard(patient?.__id, mod, modDate)
                    .then((response) => {
                        if (response.status === 200 && response.data === true) {
                            dispatch(
                                addNotesForPatient(
                                    patient.__id,
                                    patient.patient.__id,
                                    patient?.admission?.consultantEpisodeId,
                                    notes,
                                    (statusNotes) => {
                                        afterSave(dataSet);
                                        showNotification(true, statusNotes);
                                    },
                                ),
                            );
                        } else {
                            showNotification(false, false);
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        showNotification(false, false);
                    });
            }
        } else {
            dispatch(
                updatePatientData(
                    patient,
                    mod,
                    (status) => {
                        if (status) {
                            if (mod?.length === 0) {
                                afterSave(dataSet);
                            } else {
                                dispatch(
                                    addNotesForPatient(
                                        patient.__id,
                                        patient.patient.__id,
                                        patient?.admission?.consultantEpisodeId,
                                        notes,
                                        (statusNotes) => {
                                            afterSave(dataSet);
                                            showNotification(status, statusNotes);
                                        },
                                    ),
                                );
                            }
                        } else {
                            showNotification(status, false);
                        }
                    },
                    modDate,
                ),
            );
        }
    };

/**
 * Refreshes the patient data by fetching the latest updates from the server.
 *
 * @param ptObject - The patient object to be refreshed.
 * @returns - The updated patient object.
 */
export const refreshPatient = (ptObject) => async (dispatch, getState) => {
    // Configs and states
    const map = getState().wardMap.wardMapObject;

    let newPatientObject = _.cloneDeep(ptObject);
    let wardsId = map ? dispatch(getWardsIdForPatientsSearch(map.wardsId)) : ['ED', '0EDAU'];

    const ptArr = [[newPatientObject.__id, newPatientObject.objectVersion]];
    const refresh = await API.getWardsAttendancesUpdates(wardsId, ptArr);
    if (!_.isEmpty(refresh?.updated)) {
        const find = _.find(refresh.updated, (item) => item.__id === newPatientObject.__id);
        if (find) newPatientObject = find;
    }
    return newPatientObject;
};

export const handleOpenPatientIceDesktopContext = (patient) => async (dispatch, getState) => {
    const departments = getState().dicts.emergencyDepartment;
    const ptWardCode = patient?.admission?.pointOfCare ?? patient?.wardId;
    if (ptWardCode) {
        const department = _.find(departments, { wardCode: ptWardCode });
        const org = department?.iceOrganization;

        if (!org) {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'Cannot open ICE Desktop. This Urgent And Emergency Service is not linked to an ICE organisation code.',
                }),
            );
            return;
        }

        dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_ICE, { organization: org }));

        API.getICEServiceResponse(patient.patient?.__id, 'CUSTOM_AGL_OrderTest', org)
            .then((response) => {
                if (response?.url) {
                    window.open(response?.url, '_blank').focus();
                } else {
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message:
                                'Could not open ICE Desktop. The ICE service did not return a URL. Please try again, and if the problem persists contact your local administrator.',
                        }),
                    );
                }
            })
            .catch((e) => {
                console.warn(e);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message:
                            'Could not open ICE Desktop. An unexpected error occurred while contacting the ICE service. Please try again, and if the problem persists contact your local administrator.',
                    }),
                );
            });
    } else {
        dispatch(
            setSnackbar({
                type: 'warning',
                message: 'Cannot open ICE Desktop. Patient has no assigned location.',
            }),
        );
    }
};

export default patientSlice.reducer;
